import { isEmail } from "class-validator";
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";

import useShareEstimates from "hooks/api/useShareEstimates";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";

export type ShareEstimatesActionsType = {
  updateEstimatesCheckList: (estimateId: string) => void;
  updateAllEstimateCheckList: (allCheck: boolean, setAllCheck: Dispatch<SetStateAction<boolean>>) => void;
  updateInput: (value: "email" | "remarks", e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  submitShareEstimates: () => Promise<void>;
  isErrorEmailsValidation: boolean;
};

const INIT_SHARE_ESTIMATES = { email: "", estimateIds: [], remarks: "", transformedEmails: [] };

/**
 * Holds the state of sharing estimates by email on the order detail screen.
 *
 * @param estimates estimates of the order, used when every estimate is selected at once
 * @returns the share step, the share form state and the actions the share screens call
 * @example
 * const { shareEstimatesStep, shareEstimatesActions, onClickShare } = useShareEstimatesActions(estimates);
 */
const useShareEstimatesActions = (estimates: Estimate[]) => {
  const { shareEstimates: share } = useShareEstimates();
  const { showErrorModal } = useErrorModalStatusContext();

  const [shareEstimatesStep, setShareEstimatesStep] = useState<ShareEstimatesStep>(0);
  const [shareEstimates, setShareEstimates] = useState<ShareEstimates>(INIT_SHARE_ESTIMATES);

  const emailsArray = useCallback(
    () => shareEstimates.email.trim().replaceAll(" ", "").split(","),
    [shareEstimates.email]
  );
  const transformedEmails = useCallback(() => emailsArray().filter((email) => email !== ""), [emailsArray]);
  const isErrorEmails = useCallback(
    () =>
      transformedEmails()
        .map((email) => isEmail(email))
        .includes(false),
    [transformedEmails]
  );

  const isErrorEmailsValidation = useMemo(() => {
    setShareEstimates((prev) => {
      return {
        ...prev,
        transformedEmails: transformedEmails(),
      };
    });
    return isErrorEmails();
  }, [isErrorEmails, transformedEmails]);

  const updateEstimatesCheckList = useCallback(
    (estimateId: string) => {
      setShareEstimates((prev) => {
        const updatedEstimateIds = prev.estimateIds.includes(estimateId)
          ? prev.estimateIds.filter((_estimateId) => _estimateId !== estimateId)
          : [...prev.estimateIds, estimateId];
        return {
          ...prev,
          estimateIds: updatedEstimateIds,
        };
      });
    },
    [setShareEstimates]
  );

  const updateAllEstimateCheckList = useCallback(
    (allCheck: boolean, setAllCheck: Dispatch<SetStateAction<boolean>>) => {
      setShareEstimates((prev) => {
        return {
          ...prev,
          estimateIds: allCheck ? [] : estimates.map((estimate) => estimate.id),
        };
      });
      setAllCheck(!allCheck);
    },
    [setShareEstimates, estimates]
  );

  const submitShareEstimates = useCallback(async () => {
    const status = await share(shareEstimates).catch(() => showErrorModal({ httpMethod: "post" }));
    if (status === 200) {
      setShareEstimatesStep(3);
      setShareEstimates(INIT_SHARE_ESTIMATES);
    }
  }, [shareEstimates, share, showErrorModal]);

  const updateInput = useCallback(
    (value: "email" | "remarks", e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setShareEstimates((prev) => {
        return {
          ...prev,
          [value]: e.target.value,
        };
      });
    },
    [setShareEstimates]
  );

  const shareEstimatesActions: ShareEstimatesActionsType = {
    updateEstimatesCheckList,
    updateAllEstimateCheckList,
    updateInput,
    submitShareEstimates,
    isErrorEmailsValidation,
  };

  const onClickShare = () => {
    setShareEstimatesStep(1);
  };

  const setShareEstimatesStepResetScroll = (step: ShareEstimatesStep) => {
    setShareEstimatesStep(step);
    window.scrollTo(0, 0);
  };

  return {
    shareEstimatesStep,
    setShareEstimatesStepResetScroll,
    shareEstimates,
    setShareEstimates,
    shareEstimatesActions,
    onClickShare,
  };
};

export default useShareEstimatesActions;
